import * as Sentry from '@sentry/react';

function eventContainsPreloadFrame(event) {
  const values = event?.exception?.values || [];

  return values.some(value => (
    (value.stacktrace?.frames || []).some(frame => {
      const frameText = [
        frame.filename,
        frame.abs_path,
        frame.context_line,
        frame.pre_context && frame.pre_context.join('\n'),
        frame.post_context && frame.post_context.join('\n'),
      ].filter(Boolean).join('\n');

      return frameText.includes('preload-helper') ||
        frameText.includes('modulepreload') ||
        frameText.includes('type="module" crossorigin');
    })
  ));
}

function isRecoverableAssetLoadError(messageStr, event) {
  return messageStr.includes('ChunkLoadError') ||
    messageStr.includes('CSS_CHUNK_LOAD_FAILED') ||
    (messageStr.includes('Loading CSS chunk') && messageStr.includes('failed')) ||
    messageStr.includes('Unable to preload CSS for') ||
    messageStr.includes('Failed to fetch dynamically imported module') ||
    (messageStr.includes('Failed to fetch') && eventContainsPreloadFrame(event));
}

// Chrome and the Google app on iOS inject scripts into the page that WebKit attributes to the
// document URL, so their stack overflows have no frames from our JS bundles.
function eventHasOnlyInlineFrames(event) {
  const frames = (event?.exception?.values || []).flatMap(value => value.stacktrace?.frames || []);

  return frames.length > 0 && frames.every(frame => {
    const path = (frame.abs_path || frame.filename || '').split(/[?#]/)[0];
    return !path.endsWith('.js');
  });
}

function isLowSignalBrowserError(messageStr, event) {
  return messageStr.includes('Maximum call stack') &&
    (eventContainsPreloadFrame(event) || eventHasOnlyInlineFrames(event));
}

export default function sentryInitializer() {
  // need to disable uBlock Origin on localhost if testing error tracking
  Sentry.init({
    dsn: 'https://019e707473e54ce39a35d78e8ed2cd6b@o85774.ingest.sentry.io/186663',

    // CRITICAL: release must match the release used when uploading source maps
    // This is set during build via REACT_APP_SENTRY_RELEASE env var
    release: process.env.REACT_APP_SENTRY_RELEASE || process.env.REACT_APP_GIT_SHA,

    environment: process.env.NODE_ENV,

    ignoreErrors: [
      'AbortError',
      'NotSupportedError',
      'NotAllowedError',
      "Can't find variable: EmptyRanges",
      'invalid origin',
      'Request timeout for contentScriptVisibilityChanged',
      /Unhandled error response received for message <.*>/,
      'Not implemented on this platform',
    ],

    denyUrls: [
      /webkit-masked-url/,
    ],

    // Capture more context for debugging
    normalizeDepth: 5,

    beforeSend(event, hint) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      const isLocalhost = window && window.location && window.location.href.match('localhost');
      const isNgrok = window && window.location && window.location.href.match('ngrok');

      if (isDevelopment || isLocalhost || isNgrok) {
        return null; // this is equal to shouldSend false
      }

      const originalException = hint && hint.originalException;
      const originalMessage = originalException && (originalException.message || originalException);

      if (originalMessage) {
        const messageStr = String(originalMessage);

        if (isLowSignalBrowserError(messageStr, event)) {
          return null;
        }

        const isResizeObserverError =
          messageStr.includes('ResizeObserver loop') ||
          messageStr.includes('ResizeObserver loop limit exceeded');

        if (isResizeObserverError) {
          return null;
        }

        const isChunkError = isRecoverableAssetLoadError(messageStr, event);

        if (isChunkError && typeof window !== 'undefined' && window.sessionStorage) {
          const key = 'seedlang__chunk_reload_attempted_v1';
          const lastAttempt = window.sessionStorage.getItem(key);

          if (!lastAttempt) return null;

          if (Date.now() - parseInt(lastAttempt, 10) < 2000) return null;
        }
      }

      // Add extra context for stack overflow errors
      if (originalMessage && String(originalMessage).includes('Maximum call stack')) {
        event.tags = event.tags || {};
        event.tags.error_type = 'stack_overflow';
        event.extra = event.extra || {};
        event.extra.url = window && window.location && window.location.href;
        event.extra.userAgent = window && window.navigator && window.navigator.userAgent;
      }

      return event;
    },
  });
}